import { useGate, useStore, useStoreMap } from 'effector-react'
import {
  rentalContractModel,
  Form,
  useRentalContractMachineContext,
} from '~/entities/common/RentalContract'
import { usePermissions } from '~/entities/common/viewer'
import { CarOptionForRentalContract } from '~/shared/api'
import {
  daysEnumLabels,
  rentalContractQuantityOfHoursLabels,
  rentalContractTypeEnumLabels,
} from '~/shared/config/constants'
import { formatDateTimeForUI } from '~/shared/lib/date'
import { $rentalContract, Gate, update, updateFx } from './model'

type ViewRentalContractProps = {
  rentalContractId: string
  canActions?: boolean
  canEdit?: boolean
}

export function EditRentalContract({
  rentalContractId,
  canEdit = true,
}: ViewRentalContractProps) {
  useGate(Gate, { rentalContractId: rentalContractId })
  const { canRentalContractsUpdatePrice, canRentalContractsUpdate } =
    usePermissions()

  const isRentalContractLoading = useStore(
    rentalContractModel.requestFx.pending,
  )
  const isSubmitting = useStore(updateFx.pending)

  const { canStatus } = useRentalContractMachineContext()

  const defaultValues = useStoreMap($rentalContract, (rentalContract) => {
    const carId = rentalContract?.getCarId()
    const workSchedule = rentalContract?.getWorkSchedule()
    const dayOff = rentalContract?.getDayOff()
    const contractType = rentalContract?.getContractType()
    const quantityRentalPayments = rentalContract?.getQuantityRentalPayments()

    return {
      ...rentalContract?.getAttributes(),
      driverFullName: rentalContract?.getDriverFullName(),
      driverId: rentalContract?.getDriver()?.getApiId(),
      responsibleManager: rentalContract?.getResponsibleManager()?.getName(),
      carOption: carId
        ? ({
            id: carId,
            label: rentalContract?.getCarPlateNumber(),
            workRuleId: rentalContract?.getWorkRuleId(),
          } as CarOptionForRentalContract)
        : null,
      workSchedule: workSchedule
        ? {
            id: workSchedule,
            label: workSchedule,
          }
        : null,
      contractType: contractType
        ? {
            id: contractType,
            label: rentalContractTypeEnumLabels[contractType],
          }
        : null,
      dayOff: dayOff ? { id: dayOff, label: daysEnumLabels[dayOff] } : null,
      brand: rentalContract?.getCarBrandTitle(),
      model: rentalContract?.getCarModelTitle(),
      rental: rentalContract?.getCarRentalName(),
      workRule: rentalContract?.getWorkRuleTitle(),
      workRuleId: rentalContract?.getWorkRuleId(),
      rentalServicesOptions: (rentalContract?.getRentalServices() || []).map(
        (rentalService) => rentalService.getOption(),
      ),
      promotion: rentalContract?.getPromotion(),
      quantityRentalPayments: quantityRentalPayments
        ? {
            id: String(quantityRentalPayments),
            label:
              rentalContractQuantityOfHoursLabels[
                quantityRentalPayments as keyof typeof rentalContractQuantityOfHoursLabels
              ],
          }
        : null,
      signedAt: rentalContract?.getSignedAt()
        ? formatDateTimeForUI(rentalContract?.getSignedAt())
        : '',
    }
  })

  return (
    <Form
      onSuccess={update}
      isSubmitting={isSubmitting}
      isLoading={isRentalContractLoading}
      defaultValues={defaultValues}
      canEdit={
        canEdit &&
        canRentalContractsUpdate &&
        (canStatus?.formEdit || canStatus?.canFirstDayIsFreeEdit)
      }
      canFirstDayIsFreeEdit={canStatus?.canFirstDayIsFreeEdit}
      canSomeFieldEdit={canStatus?.formEdit}
      canRentalServicesEdit={canStatus?.canRentalServicesEdit}
      isBookedUntilVisible={canStatus.bookedUntilVisible}
      canUpdateRentPrice={
        canStatus.canPriceEdit && canRentalContractsUpdatePrice
      }
      canCalculateRentPerDay={canStatus.canCalculateRentPerDay}
    />
  )
}
